import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { getWhatsAppUrl } from '../utils/whatsapp';
import { Compass, CheckCircle2, MessageCircle } from 'lucide-react';
const LICENSE_PHOTOS = ['https://cdn.magicpatterns.com/uploads/nH4kXpaQ1gS37jPVArc6uQ/WhatsApp_Image_2026-06-01_at_11.13.17_p._m._(2).jpg', 'https://cdn.magicpatterns.com/uploads/xpNtq1aMa9eZLQCSc86i6X/WhatsApp_Image_2026-06-01_at_11.13.17_p._m._(1).jpg', 'https://cdn.magicpatterns.com/uploads/714mpJyChimqD1jJrs7Z9Z/WhatsApp_Image_2026-06-01_at_11.13.17_p._m..jpg', 'https://cdn.magicpatterns.com/uploads/4Q79AdQ6AVmFaRgDugxaS7/WhatsApp_Image_2026-06-01_at_11.13.16_p._m._(2).jpg', 'https://cdn.magicpatterns.com/uploads/wjgpHh7rfnyszypPnPiicP/WhatsApp_Image_2026-06-01_at_11.13.16_p._m._(1).jpg'];
export const LicenseSection = () => {
  const {
    t
  } = useLanguage();
  const [photoIndex, setPhotoIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setPhotoIndex(p => (p + 1) % LICENSE_PHOTOS.length);
    }, 4000);
    return () => clearInterval(id);
  }, []);
  const handleWhatsApp = () => {
    const message = t('wa.license');
    window.open(getWhatsAppUrl(message), '_blank');
  };
  const features = [1, 2, 3, 4];
  return <section id="licencias" className="py-12 sm:py-16 md:py-20 bg-card" data-id="element-437">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-438">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.7
      }} className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100" data-id="element-439">
          <div className="flex flex-col lg:flex-row" data-id="element-440">
            {/* Carousel: Real students obtaining their license */}
            <div className="lg:w-5/12 relative min-h-[320px] sm:min-h-[420px] lg:min-h-[560px] bg-navy overflow-hidden" data-id="element-441">
              <AnimatePresence mode="wait" data-id="element-442">
                <motion.img key={photoIndex} src={LICENSE_PHOTOS[photoIndex]} alt="Alumno con licencia náutica obtenida en Torrevieja" initial={{
                opacity: 0,
                scale: 1.05
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 1
              }} transition={{
                duration: 0.8,
                ease: 'easeOut'
              }} className="absolute inset-0 w-full h-full object-cover" data-id="element-443" />
              </AnimatePresence>

              {/* Soft gradient for badge/legibility */}
              <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent pointer-events-none" data-id="element-444" />

              {/* Badge */}
              <div className="absolute top-5 left-5" data-id="element-445">
                <div className="inline-flex items-center space-x-2 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-bold text-navy shadow-md" data-id="element-446">
                  <Compass className="w-3.5 h-3.5 text-turquoise" data-id="element-447" />
                  <span data-id="element-448">Alumnos reales · Torrevieja</span>
                </div>
              </div>

              {/* Dot indicators */}
              <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex space-x-2" data-id="element-449">
                {LICENSE_PHOTOS.map((_, i) => <button key={i} onClick={() => setPhotoIndex(i)} aria-label={`Foto ${i + 1}`} className={`h-1.5 rounded-full transition-all ${i === photoIndex ? 'w-7 bg-turquoise' : 'w-1.5 bg-white/60'}`} data-id="element-450" />)}
              </div>
            </div>

            <div className="lg:w-7/12 p-6 sm:p-8 md:p-12 lg:p-16" data-id="element-451">
              <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-turquoise/10 text-turquoise mb-4 sm:mb-6" data-id="element-452">
                <Compass className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-453" />
              </div>

              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-navy mb-4 sm:mb-6" data-id="element-454">
                {t('license.title')}
              </h2>

              <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 leading-relaxed" data-id="element-455">
                {t('license.desc')}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10" data-id="element-456">
                {features.map(num => <div key={num} className="flex items-start space-x-3" data-id="element-457">
                    <CheckCircle2 className="w-5 h-5 text-turquoise flex-shrink-0 mt-0.5" data-id="element-458" />
                    <span className="text-gray-700 font-medium" data-id="element-459">
                      {t(`license.f${num}`)}
                    </span>
                  </div>)}
              </div>

              <button onClick={handleWhatsApp} className="bg-navy hover:bg-navy-light text-white font-bold py-4 px-8 rounded-full transition-all flex items-center justify-center space-x-2 w-full sm:w-auto uppercase tracking-wide text-sm" data-id="element-460">
                <MessageCircle className="w-5 h-5 text-turquoise" data-id="element-461" />
                <span data-id="element-462">{t('license.cta')}</span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
};